// =========================================================================
// Self-hosted pageview analytics service layer.
//
// Wraps the SECURITY DEFINER SDFs in `app_admin.*` (platform-wide stats)
// and `app_vendor.*` (per-vendor / per-product stats) with one typed
// callable per SDF. Authorization happens inside the function bodies —
// these wrappers are intentionally thin.
//
// All SDFs use a positive (`p_from`, `p_to`) timestamp range and a
// `p_exclude_bots` flag defaulting to true. We pass timestamps as ISO
// strings; PostgreSQL coerces them to timestamptz.
// =========================================================================

import { supabase } from '../client';

// ---------- shared types ----------

export type AnalyticsGranularity = 'hour' | 'day';

export interface PageviewSummary {
  total_views: number;
  unique_sessions: number;
  unique_ips: number;
  prior_views: number;
  pct_change: number | null;
  from: string;
  to: string;
}

export interface PageviewTimeseriesPoint {
  bucket: string;
  views: number;
  unique_sessions: number;
  unique_ips: number;
}

export interface TopPageRow {
  app: string;
  path_template: string;
  views: number;
  unique_sessions: number;
}

export interface TopVendorRow {
  vendor_id: string;
  vendor_slug: string;
  vendor_company_name: string | null;
  views: number;
  unique_sessions: number;
}

export interface TopProductRow {
  product_id: string;
  product_name: string;
  vendor_slug: string | null;
  views: number;
}

export interface BreakdownRow {
  value: string;
  views: number;
  unique_sessions?: number;
}

export interface VendorTopProductRow {
  product_id: string;
  product_name: string;
  product_slug: string;
  views: number;
  unique_sessions: number;
  recent_orders: number;
  recommend_push: boolean;
}

export interface AnalyticsRangeArgs {
  from: Date | string;
  to: Date | string;
  excludeBots?: boolean;
}

function isoString(value: Date | string): string {
  if (typeof value === 'string') return value;
  return value.toISOString();
}

// ---------- app_admin.* ----------

/**
 * Platform-wide summary over [from, to). Prior period is same-length
 * window immediately before `from`, used for the pct-change banner on
 * the dashboard.
 */
export async function getPageviewSummary(args: AnalyticsRangeArgs): Promise<PageviewSummary> {
  const { data, error } = await (supabase() as any)
    .schema('app_admin')
    .rpc('get_pageview_summary', {
      p_from: isoString(args.from),
      p_to: isoString(args.to),
      p_exclude_bots: args.excludeBots ?? true,
    });
  if (error) throw error;
  return normalizeSummary(data, isoString(args.from), isoString(args.to));
}

export interface PageviewTimeseriesArgs extends AnalyticsRangeArgs {
  granularity: AnalyticsGranularity;
  app?: string;
  pathTemplate?: string;
  vendorId?: string;
}

export async function getPageviewTimeseries(
  args: PageviewTimeseriesArgs,
): Promise<PageviewTimeseriesPoint[]> {
  const { data, error } = await (supabase() as any)
    .schema('app_admin')
    .rpc('get_pageview_timeseries', {
      p_from: isoString(args.from),
      p_to: isoString(args.to),
      p_granularity: args.granularity,
      p_app: args.app ?? null,
      p_path_template: args.pathTemplate ?? null,
      p_vendor_id: args.vendorId ?? null,
      p_exclude_bots: args.excludeBots ?? true,
    });
  if (error) throw error;
  return Array.isArray(data) ? data.map(normalizeTimeseries) : [];
}

export interface TopListArgs extends AnalyticsRangeArgs {
  limit?: number;
}

export async function getTopPages(args: TopListArgs): Promise<TopPageRow[]> {
  const { data, error } = await (supabase() as any)
    .schema('app_admin')
    .rpc('get_top_pages', {
      p_from: isoString(args.from),
      p_to: isoString(args.to),
      p_limit: clampLimit(args.limit, 20, 200),
      p_exclude_bots: args.excludeBots ?? true,
    });
  if (error) throw error;
  return Array.isArray(data) ? data.map(normalizeTopPage) : [];
}

export async function getTopVendors(args: TopListArgs): Promise<TopVendorRow[]> {
  const { data, error } = await (supabase() as any)
    .schema('app_admin')
    .rpc('get_top_vendors', {
      p_from: isoString(args.from),
      p_to: isoString(args.to),
      p_limit: clampLimit(args.limit, 20, 200),
      p_exclude_bots: args.excludeBots ?? true,
    });
  if (error) throw error;
  return Array.isArray(data) ? data.map(normalizeTopVendor) : [];
}

export async function getTopProducts(args: TopListArgs): Promise<TopProductRow[]> {
  const { data, error } = await (supabase() as any)
    .schema('app_admin')
    .rpc('get_top_products', {
      p_from: isoString(args.from),
      p_to: isoString(args.to),
      p_limit: clampLimit(args.limit, 20, 200),
      p_exclude_bots: args.excludeBots ?? true,
    });
  if (error) throw error;
  return Array.isArray(data) ? data.map(normalizeTopProduct) : [];
}

export async function getReferrerBreakdown(args: TopListArgs): Promise<BreakdownRow[]> {
  const { data, error } = await (supabase() as any)
    .schema('app_admin')
    .rpc('get_referrer_breakdown', {
      p_from: isoString(args.from),
      p_to: isoString(args.to),
      p_limit: clampLimit(args.limit, 20, 200),
      p_exclude_bots: args.excludeBots ?? true,
    });
  if (error) throw error;
  return Array.isArray(data)
    ? data.map((r: { referrer_host: string; views: number; unique_sessions?: number }) => ({
        value: r.referrer_host ?? '(direct)',
        views: numberOr(r.views, 0),
        unique_sessions: numberOr(r.unique_sessions, 0),
      }))
    : [];
}

export async function getCountryBreakdown(args: TopListArgs): Promise<BreakdownRow[]> {
  const { data, error } = await (supabase() as any)
    .schema('app_admin')
    .rpc('get_country_breakdown', {
      p_from: isoString(args.from),
      p_to: isoString(args.to),
      p_limit: clampLimit(args.limit, 50, 250),
      p_exclude_bots: args.excludeBots ?? true,
    });
  if (error) throw error;
  return Array.isArray(data)
    ? data.map((r: { country_iso2: string; views: number }) => ({
        value: r.country_iso2 ?? '??',
        views: numberOr(r.views, 0),
      }))
    : [];
}

export async function getUaBreakdown(args: AnalyticsRangeArgs): Promise<BreakdownRow[]> {
  const { data, error } = await (supabase() as any)
    .schema('app_admin')
    .rpc('get_ua_breakdown', {
      p_from: isoString(args.from),
      p_to: isoString(args.to),
      p_exclude_bots: args.excludeBots ?? true,
    });
  if (error) throw error;
  return Array.isArray(data)
    ? data.map((r: { ua_family: string; views: number }) => ({
        value: r.ua_family ?? 'unknown',
        views: numberOr(r.views, 0),
      }))
    : [];
}

export type UtmDimension = 'source' | 'medium' | 'campaign' | 'term' | 'content';

export interface UtmBreakdownArgs extends TopListArgs {
  dimension: UtmDimension;
}

export async function getUtmBreakdown(args: UtmBreakdownArgs): Promise<BreakdownRow[]> {
  const { data, error } = await (supabase() as any)
    .schema('app_admin')
    .rpc('get_utm_breakdown', {
      p_from: isoString(args.from),
      p_to: isoString(args.to),
      p_dimension: args.dimension,
      p_limit: clampLimit(args.limit, 20, 100),
      p_exclude_bots: args.excludeBots ?? true,
    });
  if (error) throw error;
  return Array.isArray(data)
    ? data.map((r: { value: string; views: number }) => ({
        value: r.value ?? '(none)',
        views: numberOr(r.views, 0),
      }))
    : [];
}

// ---------- app_vendor.* ----------

export interface VendorRangeArgs extends AnalyticsRangeArgs {
  vendorId: string;
}

export async function getVendorPageviewSummary(args: VendorRangeArgs): Promise<PageviewSummary> {
  const { data, error } = await (supabase() as any)
    .schema('app_vendor')
    .rpc('get_vendor_pageview_summary', {
      p_vendor_id: args.vendorId,
      p_from: isoString(args.from),
      p_to: isoString(args.to),
      p_exclude_bots: args.excludeBots ?? true,
    });
  if (error) throw error;
  return normalizeSummary(data, isoString(args.from), isoString(args.to));
}

export interface VendorTimeseriesArgs extends VendorRangeArgs {
  granularity: AnalyticsGranularity;
}

export async function getVendorPageviewTimeseries(
  args: VendorTimeseriesArgs,
): Promise<PageviewTimeseriesPoint[]> {
  const { data, error } = await (supabase() as any)
    .schema('app_vendor')
    .rpc('get_vendor_pageview_timeseries', {
      p_vendor_id: args.vendorId,
      p_from: isoString(args.from),
      p_to: isoString(args.to),
      p_granularity: args.granularity,
      p_exclude_bots: args.excludeBots ?? true,
    });
  if (error) throw error;
  return Array.isArray(data) ? data.map(normalizeTimeseries) : [];
}

export interface VendorTopProductsArgs extends VendorRangeArgs {
  limit?: number;
}

export async function getVendorTopProducts(
  args: VendorTopProductsArgs,
): Promise<VendorTopProductRow[]> {
  const { data, error } = await (supabase() as any)
    .schema('app_vendor')
    .rpc('get_vendor_top_products', {
      p_vendor_id: args.vendorId,
      p_from: isoString(args.from),
      p_to: isoString(args.to),
      p_limit: clampLimit(args.limit, 20, 100),
      p_exclude_bots: args.excludeBots ?? true,
    });
  if (error) throw error;
  return Array.isArray(data)
    ? data.map((r: {
        product_id: string;
        product_name: string;
        product_slug: string;
        views: number;
        unique_sessions: number;
        recent_orders: number;
        recommend_push: boolean;
      }) => ({
        product_id: r.product_id,
        product_name: r.product_name,
        product_slug: r.product_slug,
        views: numberOr(r.views, 0),
        unique_sessions: numberOr(r.unique_sessions, 0),
        recent_orders: numberOr(r.recent_orders, 0),
        recommend_push: Boolean(r.recommend_push),
      }))
    : [];
}

export async function getVendorReferrerBreakdown(
  args: VendorTopProductsArgs,
): Promise<BreakdownRow[]> {
  const { data, error } = await (supabase() as any)
    .schema('app_vendor')
    .rpc('get_vendor_referrer_breakdown', {
      p_vendor_id: args.vendorId,
      p_from: isoString(args.from),
      p_to: isoString(args.to),
      p_limit: clampLimit(args.limit, 20, 100),
      p_exclude_bots: args.excludeBots ?? true,
    });
  if (error) throw error;
  return Array.isArray(data)
    ? data.map((r: { referrer_host: string; views: number; unique_sessions: number }) => ({
        value: r.referrer_host ?? '(direct)',
        views: numberOr(r.views, 0),
        unique_sessions: numberOr(r.unique_sessions, 0),
      }))
    : [];
}

export async function getVendorCountryBreakdown(
  args: VendorTopProductsArgs,
): Promise<BreakdownRow[]> {
  const { data, error } = await (supabase() as any)
    .schema('app_vendor')
    .rpc('get_vendor_country_breakdown', {
      p_vendor_id: args.vendorId,
      p_from: isoString(args.from),
      p_to: isoString(args.to),
      p_limit: clampLimit(args.limit, 50, 250),
      p_exclude_bots: args.excludeBots ?? true,
    });
  if (error) throw error;
  return Array.isArray(data)
    ? data.map((r: { country_iso2: string; views: number }) => ({
        value: r.country_iso2 ?? '??',
        views: numberOr(r.views, 0),
      }))
    : [];
}

export interface ProductRangeArgs extends AnalyticsRangeArgs {
  productId: string;
}

export async function getProductPageviewSummary(args: ProductRangeArgs): Promise<PageviewSummary> {
  const { data, error } = await (supabase() as any)
    .schema('app_vendor')
    .rpc('get_product_pageview_summary', {
      p_product_id: args.productId,
      p_from: isoString(args.from),
      p_to: isoString(args.to),
      p_exclude_bots: args.excludeBots ?? true,
    });
  if (error) throw error;
  return normalizeSummary(data, isoString(args.from), isoString(args.to));
}

export interface ProductTimeseriesArgs extends ProductRangeArgs {
  granularity: AnalyticsGranularity;
}

export async function getProductPageviewTimeseries(
  args: ProductTimeseriesArgs,
): Promise<PageviewTimeseriesPoint[]> {
  const { data, error } = await (supabase() as any)
    .schema('app_vendor')
    .rpc('get_product_pageview_timeseries', {
      p_product_id: args.productId,
      p_from: isoString(args.from),
      p_to: isoString(args.to),
      p_granularity: args.granularity,
      p_exclude_bots: args.excludeBots ?? true,
    });
  if (error) throw error;
  return Array.isArray(data) ? data.map(normalizeTimeseries) : [];
}

export interface ProductReferrerArgs extends ProductRangeArgs {
  limit?: number;
}

export async function getProductReferrerBreakdown(
  args: ProductReferrerArgs,
): Promise<BreakdownRow[]> {
  const { data, error } = await (supabase() as any)
    .schema('app_vendor')
    .rpc('get_product_referrer_breakdown', {
      p_product_id: args.productId,
      p_from: isoString(args.from),
      p_to: isoString(args.to),
      p_limit: clampLimit(args.limit, 20, 100),
      p_exclude_bots: args.excludeBots ?? true,
    });
  if (error) throw error;
  return Array.isArray(data)
    ? data.map((r: { referrer_host: string; views: number; unique_sessions: number }) => ({
        value: r.referrer_host ?? '(direct)',
        views: numberOr(r.views, 0),
        unique_sessions: numberOr(r.unique_sessions, 0),
      }))
    : [];
}

// ---------- normalizers ----------

function normalizeSummary(raw: unknown, from: string, to: string): PageviewSummary {
  const r = (raw && typeof raw === 'object' ? raw : {}) as Record<string, unknown>;
  return {
    total_views: numberOr(r.total_views, 0),
    unique_sessions: numberOr(r.unique_sessions, 0),
    unique_ips: numberOr(r.unique_ips, 0),
    prior_views: numberOr(r.prior_views, 0),
    pct_change:
      r.pct_change === null || r.pct_change === undefined ? null : numberOr(r.pct_change, 0),
    from: typeof r.from === 'string' ? r.from : from,
    to: typeof r.to === 'string' ? r.to : to,
  };
}

function normalizeTimeseries(row: unknown): PageviewTimeseriesPoint {
  const r = (row && typeof row === 'object' ? row : {}) as Record<string, unknown>;
  return {
    bucket: typeof r.bucket === 'string' ? r.bucket : new Date(0).toISOString(),
    views: numberOr(r.views, 0),
    unique_sessions: numberOr(r.unique_sessions, 0),
    unique_ips: numberOr(r.unique_ips, 0),
  };
}

function normalizeTopPage(row: unknown): TopPageRow {
  const r = (row && typeof row === 'object' ? row : {}) as Record<string, unknown>;
  return {
    app: typeof r.app === 'string' ? r.app : 'unknown',
    path_template: typeof r.path_template === 'string' ? r.path_template : '/',
    views: numberOr(r.views, 0),
    unique_sessions: numberOr(r.unique_sessions, 0),
  };
}

function normalizeTopVendor(row: unknown): TopVendorRow {
  const r = (row && typeof row === 'object' ? row : {}) as Record<string, unknown>;
  return {
    vendor_id: typeof r.vendor_id === 'string' ? r.vendor_id : '',
    vendor_slug: typeof r.vendor_slug === 'string' ? r.vendor_slug : '',
    vendor_company_name:
      typeof r.vendor_company_name === 'string' && r.vendor_company_name.length > 0
        ? r.vendor_company_name
        : null,
    views: numberOr(r.views, 0),
    unique_sessions: numberOr(r.unique_sessions, 0),
  };
}

function normalizeTopProduct(row: unknown): TopProductRow {
  const r = (row && typeof row === 'object' ? row : {}) as Record<string, unknown>;
  return {
    product_id: typeof r.product_id === 'string' ? r.product_id : '',
    product_name: typeof r.product_name === 'string' ? r.product_name : '(unknown)',
    vendor_slug: typeof r.vendor_slug === 'string' && r.vendor_slug.length > 0 ? r.vendor_slug : null,
    views: numberOr(r.views, 0),
  };
}

function numberOr(value: unknown, fallback: number): number {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string' && value.trim() !== '') {
    const n = Number(value);
    if (Number.isFinite(n)) return n;
  }
  return fallback;
}

function clampLimit(value: number | undefined, fallback: number, max: number): number {
  const n = typeof value === 'number' && Number.isFinite(value) ? Math.trunc(value) : fallback;
  if (n < 1) return 1;
  if (n > max) return max;
  return n;
}
